import { useState, useEffect } from 'react';
import { useTenant } from '../contexts/TenantContext';
import {
  BarChart3,
  MessageSquare,
  Mail,
  Users,
  Send,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  XCircle,
  RefreshCw,
  DollarSign,
  ShoppingCart,
  AlertTriangle,
  MousePointerClick,
  Eye,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line
} from 'recharts';

import { Skeleton } from '@/components/ui/skeleton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AnalyticsPage() {
  const { getToken } = useTenant();
  const [overview, setOverview] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [trends, setTrends] = useState([]);
  const [roiSummary, setRoiSummary] = useState(null);
  const [roiCampaigns, setRoiCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('roi'); // 'roi' or 'delivery'

  // Channel toggle
  const [channel, setChannel] = useState('sms'); // 'sms' or 'email'

  // Email analytics data
  const [emailOverview, setEmailOverview] = useState(null);
  const [emailTrends, setEmailTrends] = useState([]);
  const [emailLoading, setEmailLoading] = useState(false);
  const emailFetched = useState(false);

  const fetchData = async () => {
    try {
      const token = await getToken();
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      const [overviewRes, campaignsRes, trendsRes, roiSummaryRes, roiCampaignsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/analytics/overview`, { headers }),
        fetch(`${BACKEND_URL}/api/analytics/campaigns?page_size=10`, { headers }),
        fetch(`${BACKEND_URL}/api/analytics/trends?months=6`, { headers }),
        fetch(`${BACKEND_URL}/api/roi/summary`, { headers }),
        fetch(`${BACKEND_URL}/api/roi/campaigns`, { headers }),
      ]);

      if (!overviewRes.ok) throw new Error('Failed to fetch analytics overview');
      const overviewData = await overviewRes.json();
      setOverview(overviewData);

      if (campaignsRes.ok) {
        const campaignsData = await campaignsRes.json();
        setCampaigns(campaignsData.campaigns || []);
      }

      if (trendsRes.ok) {
        const trendsData = await trendsRes.json();
        setTrends(trendsData.monthly_trends || []);
      }

      if (roiSummaryRes.ok) {
        const roiSummaryData = await roiSummaryRes.json();
        setRoiSummary(roiSummaryData);
      }
      if (roiCampaignsRes.ok) {
        const roiCampaignsData = await roiCampaignsRes.json();
        setRoiCampaigns(roiCampaignsData);
      }

    } catch (err) {
      console.error('Analytics fetch error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchEmailData = async () => {
    setEmailLoading(true);
    try {
      const token = await getToken();
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      const [overviewRes, trendsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/analytics/email-overview`, { headers }),
        fetch(`${BACKEND_URL}/api/analytics/email-trends?months=6`, { headers }),
      ]);

      if (overviewRes.ok) setEmailOverview(await overviewRes.json());
      if (trendsRes.ok) {
        const data = await trendsRes.json();
        setEmailTrends(data.monthly_trends || []);
      }
    } catch (err) {
      console.error('Email analytics fetch error:', err);
    } finally {
      setEmailLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [getToken]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch email data on first switch to email channel
  useEffect(() => {
    if (channel === 'email' && !emailFetched[0]) {
      emailFetched[0] = true;
      fetchEmailData();
    }
  }, [channel]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
    if (channel === 'email') fetchEmailData();
  };

  if (loading) {
    return (
      <div className="p-4 lg:p-8">
        <div className="flex justify-between items-center mb-8">
            <div>
                <Skeleton className="h-8 w-48 mb-2" />
                <Skeleton className="h-4 w-64" />
            </div>
            <Skeleton className="h-10 w-24 rounded-lg" />
        </div>
        <div className="border-b border-gray-200 mb-6 flex gap-8">
             <Skeleton className="h-10 w-32" />
             <Skeleton className="h-10 w-32" />
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-8">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 lg:p-6 h-32 flex flex-col justify-between">
              <Skeleton className="h-12 w-12 rounded-xl" />
              <div>
                <Skeleton className="h-8 w-24 mb-2" />
                <Skeleton className="h-4 w-16" />
              </div>
            </div>
          ))}
        </div>
        <Skeleton className="h-96 w-full rounded-xl" />
      </div>
    );
  }


  if (error) {
    return (
      <div className="p-4 lg:p-8">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <AlertCircle className="w-6 h-6 text-red-500" />
            <div>
              <h3 className="font-medium text-red-800">Error loading analytics</h3>
              <p className="text-sm text-red-600">{error}</p>
            </div>
          </div>
          <button
            onClick={() => { setError(null); setLoading(true); fetchData(); }}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium flex-shrink-0"
          >
            <RefreshCw className="w-4 h-4" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  // --- ROI STATS ---
  const roiPercentage = roiSummary?.roi_percentage || 0;
  const netRevenueCents = roiSummary?.net_revenue_cents || 0;

  const roiStats = [
    { name: 'Total Revenue', value: `$${((roiSummary?.total_revenue_cents || 0) / 100).toFixed(2)}`, icon: DollarSign, color: 'green', description: `${roiSummary?.total_orders || 0} orders attributed` },
    { name: 'Net Return', value: netRevenueCents >= 0 ? `$${(netRevenueCents / 100).toFixed(2)}` : 'N/A', icon: TrendingUp, color: netRevenueCents >= 0 ? 'blue' : 'gray', description: 'Revenue - SMS Cost' },
    { name: 'ROI', value: roiPercentage >= 0 ? `${roiPercentage}%` : 'N/A', icon: BarChart3, color: roiPercentage >= 0 ? 'purple' : 'gray', description: 'Return on Investment' },
    { name: 'Avg Order Value', value: `$${((roiSummary?.avg_order_value_cents || 0) / 100).toFixed(2)}`, icon: ShoppingCart, color: 'indigo', description: 'Per attributed order' },
  ];

  // --- SMS DELIVERY STATS ---
  const smsDeliveryStats = [
    { name: 'SMS Sent', value: overview?.sms_sent_this_month?.toLocaleString() || '0', icon: MessageSquare, color: 'blue', description: `of ${overview?.sms_limit?.toLocaleString() || '0'} limit` },
    { name: 'Remaining', value: overview?.sms_remaining?.toLocaleString() || '0', icon: TrendingUp, color: overview?.sms_remaining > 100 ? 'green' : 'orange', description: 'until limit reached' },
    { name: 'Campaigns', value: overview?.campaigns_sent_this_month?.toLocaleString() || '0', icon: Send, color: 'purple', description: `${overview?.total_campaigns || 0} total` },
    { name: 'Customers', value: overview?.active_customers?.toLocaleString() || '0', icon: Users, color: 'indigo', description: `${overview?.opt_out_rate || 0}% opt-out rate` },
  ];

  // --- EMAIL DELIVERY STATS ---
  const emailDeliveryStats = [
    { name: 'Emails Sent', value: (emailOverview?.total_sent || 0).toLocaleString(), icon: Mail, color: 'purple', description: `${emailOverview?.email_campaigns_this_month || 0} campaigns this month` },
    { name: 'Delivered', value: (emailOverview?.total_delivered || 0).toLocaleString(), icon: CheckCircle, color: 'green', description: `${emailOverview?.open_rate || 0}% open rate` },
    { name: 'Bounced', value: (emailOverview?.total_bounced || 0).toLocaleString(), icon: AlertTriangle, color: 'orange', description: `${emailOverview?.bounce_rate || 0}% bounce rate` },
    { name: 'Complaints', value: (emailOverview?.total_complained || 0).toLocaleString(), icon: XCircle, color: 'red', description: `${emailOverview?.total_failed || 0} failed` },
  ];

  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    orange: 'bg-orange-100 text-orange-600',
    purple: 'bg-purple-100 text-purple-600',
    indigo: 'bg-indigo-100 text-indigo-600',
    red: 'bg-red-100 text-red-600',
    gray: 'bg-gray-100 text-gray-600',
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-700';
      case 'running': return 'bg-blue-100 text-blue-700';
      case 'failed': return 'bg-red-100 text-red-700';
      case 'draft': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const currentDeliveryStats = channel === 'email' ? emailDeliveryStats : smsDeliveryStats;

  return (
    <div className="p-4 lg:p-8" data-testid="analytics-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 lg:mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
          <p className="text-gray-600 mt-1">Track your campaigns and ROI</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Channel Toggle */}
          <div className="inline-flex items-center rounded-lg border border-gray-200 p-1 bg-white">
            <button
              onClick={() => setChannel('sms')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                channel === 'sms'
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <MessageSquare className="w-3.5 h-3.5" />
              SMS
            </button>
            <button
              onClick={() => setChannel('email')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                channel === 'email'
                  ? 'bg-purple-100 text-purple-700'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Mail className="w-3.5 h-3.5" />
              Email
            </button>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 text-sm"
            data-testid="refresh-analytics-btn"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('roi')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'roi'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            ROI & Revenue
          </button>
          <button
            onClick={() => setActiveTab('delivery')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'delivery'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Delivery & Volume
          </button>
        </nav>
      </div>

      {activeTab === 'roi' ? (
        /* ================= ROI TAB ================= */
        <>
          {channel === 'email' ? (
            /* Email ROI placeholder */
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
              <Mail className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Email ROI tracking coming soon</h3>
              <p className="text-gray-500">Email campaigns don't consume SMS credits. Revenue attribution for email campaigns is in development.</p>
            </div>
          ) : (
            <>
              {/* ROI Overview Stats */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-8">
                {roiStats.map((stat) => {
                  const Icon = stat.icon;
                  return (
                    <div key={stat.name} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 lg:p-6">
                      <div className="flex items-center justify-between mb-3 lg:mb-4">
                        <div className={`w-10 h-10 lg:w-12 lg:h-12 rounded-xl ${colorClasses[stat.color]} flex items-center justify-center`}>
                          <Icon className="w-5 h-5 lg:w-6 lg:h-6" />
                        </div>
                      </div>
                      <div className="text-xl lg:text-3xl font-bold text-gray-900 mb-1">{stat.value}</div>
                      <div className="text-sm text-gray-500 font-medium">{stat.name}</div>
                      <div className="text-xs text-gray-400 mt-1 truncate">{stat.description}</div>
                    </div>
                  );
                })}
              </div>

              {/* ROI Explanation */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="space-y-2">
                  <h3 className="font-semibold text-blue-900 text-sm">How ROI is measured</h3>
                  <p className="text-sm text-blue-700">
                    Revenue is tracked when a <strong>promo code</strong> sent in an SMS campaign is used in an online order within <strong>7 days</strong> of sending.
                  </p>
                  <p className="text-xs text-blue-600 mt-2">
                    💡 <strong>Campaign costs</strong> are calculated based on the SMS credits used from your prepaid packs. Costs are locked in at purchase time and never recalculated.
                  </p>
                </div>
              </div>

              {/* ROI Campaign Breakdown Table */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-4 lg:p-6 border-b border-gray-100">
                  <h2 className="text-lg font-semibold text-gray-900">ROI by Campaign</h2>
                  <p className="text-sm text-gray-500 mt-1">Revenue performance for your campaigns</p>
                </div>
                {roiCampaigns.length === 0 ? (
                  <div className="p-8 text-center">
                    <DollarSign className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No attributed revenue yet</p>
                    <p className="text-sm text-gray-400 mt-1">Ensure your campaigns include promo codes and your ordering system is connected.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Campaign</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Promo Code</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Orders</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Revenue</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">SMS Cost</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Net Return</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">ROI %</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {roiCampaigns.map((campaign) => (
                          <tr key={campaign.campaign_id} className="hover:bg-gray-50">
                            <td className="px-6 py-4">
                              <div className="text-sm font-medium text-gray-900">{campaign.campaign_name}</div>
                              <div className="text-xs text-gray-500">{new Date(campaign.sent_at).toLocaleDateString('en-AU')}</div>
                            </td>
                            <td className="px-6 py-4">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-mono font-medium bg-gray-100 text-gray-800 border border-gray-200">{campaign.promo_code || '—'}</span>
                            </td>
                            <td className="px-6 py-4 text-right text-sm text-gray-900">{campaign.orders_count}</td>
                            <td className="px-6 py-4 text-right text-sm text-green-600 font-medium">${(campaign.revenue_cents / 100).toFixed(2)}</td>
                            <td className="px-6 py-4 text-right text-sm text-gray-500">${(campaign.sms_cost_cents / 100).toFixed(2)}</td>
                            <td className={`px-6 py-4 text-right text-sm font-medium ${campaign.net_revenue_cents >= 0 ? 'text-blue-600' : 'text-gray-500'}`}>
                              {campaign.net_revenue_cents >= 0 ? `$${(campaign.net_revenue_cents / 100).toFixed(2)}` : 'N/A'}
                            </td>
                            <td className="px-6 py-4 text-right">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${campaign.roi_percentage >= 0 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                                {campaign.roi_percentage >= 0 ? `${campaign.roi_percentage.toFixed(0)}%` : 'N/A'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </>
      ) : (
        /* ================= DELIVERY TAB ================= */
        <>
          {/* Overview Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-8">
            {(channel === 'email' && emailLoading) ? (
              [1,2,3,4].map(i => (
                <div key={i} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 lg:p-6 h-32 flex flex-col justify-between">
                  <Skeleton className="h-12 w-12 rounded-xl" />
                  <div><Skeleton className="h-8 w-24 mb-2" /><Skeleton className="h-4 w-16" /></div>
                </div>
              ))
            ) : (
              currentDeliveryStats.map((stat) => {
                const Icon = stat.icon;
                return (
                  <div key={stat.name} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 lg:p-6">
                    <div className="flex items-center justify-between mb-3 lg:mb-4">
                      <div className={`w-10 h-10 lg:w-12 lg:h-12 rounded-xl ${colorClasses[stat.color]} flex items-center justify-center`}>
                        <Icon className="w-5 h-5 lg:w-6 lg:h-6" />
                      </div>
                    </div>
                    <div className="text-xl lg:text-3xl font-bold text-gray-900 mb-1">{stat.value}</div>
                    <div className="text-sm text-gray-500 font-medium">{stat.name}</div>
                    <div className="text-xs text-gray-400 mt-1 truncate">{stat.description}</div>
                  </div>
                );
              })
            )}
          </div>

          {channel === 'sms' ? (
            /* SMS Charts & Table */
            <>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-100 p-4 lg:p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">SMS Volume Trend</h2>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={trends}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} width={30} />
                        <Tooltip contentStyle={{ backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #e5e7eb' }} labelStyle={{ fontWeight: 'bold', color: '#111827' }} />
                        <Bar dataKey="sms_sent" name="SMS Sent" fill="#f97316" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 lg:p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Delivery Rate Trend</h2>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={trends}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} domain={[0, 100]} width={30} />
                        <Tooltip contentStyle={{ backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #e5e7eb' }} labelStyle={{ fontWeight: 'bold', color: '#111827' }} />
                        <Line type="monotone" dataKey="delivery_rate" name="Delivery %" stroke="#22c55e" strokeWidth={2} dot={{ fill: '#22c55e', r: 4 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>

              {/* Campaign Analytics Table */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-4 lg:p-6 border-b border-gray-100">
                  <h2 className="text-lg font-semibold text-gray-900">Recent Campaigns</h2>
                  <p className="text-sm text-gray-500 mt-1">Delivery metrics for your recent campaigns</p>
                </div>
                {campaigns.length === 0 ? (
                  <div className="p-8 text-center">
                    <BarChart3 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No campaigns yet</p>
                    <p className="text-sm text-gray-400 mt-1">Create your first campaign to see analytics</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Campaign</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Sent</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Delivered</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Failed</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Delivery %</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {campaigns.map((campaign) => (
                          <tr key={campaign.campaign_id} className="hover:bg-gray-50">
                            <td className="px-6 py-4">
                              <div className="text-sm font-medium text-gray-900">{campaign.campaign_name}</div>
                              <div className="text-xs text-gray-500">{new Date(campaign.created_at).toLocaleDateString('en-AU')}</div>
                            </td>
                            <td className="px-6 py-4">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(campaign.status)}`}>{campaign.status}</span>
                            </td>
                            <td className="px-6 py-4 text-right text-sm text-gray-900">{campaign.sent_count.toLocaleString()}</td>
                            <td className="px-6 py-4 text-right text-sm text-green-600 font-medium">{campaign.delivered_count.toLocaleString()}</td>
                            <td className="px-6 py-4 text-right text-sm text-red-600 font-medium">{campaign.failed_count.toLocaleString()}</td>
                            <td className="px-6 py-4 text-right">
                              <div className="flex items-center justify-end gap-2">
                                <div className="w-16 h-2 bg-gray-100 rounded-full overflow-hidden">
                                  <div className={`h-full rounded-full ${campaign.delivery_rate >= 90 ? 'bg-green-500' : campaign.delivery_rate >= 70 ? 'bg-yellow-500' : 'bg-red-500'}`} style={{ width: `${campaign.delivery_rate}%` }} />
                                </div>
                                <span className="text-sm text-gray-900 w-12">{campaign.delivery_rate}%</span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          ) : (
            /* Email Charts */
            <>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-100 p-4 lg:p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Email Volume Trend</h2>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={emailTrends}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} width={30} />
                        <Tooltip contentStyle={{ backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #e5e7eb' }} labelStyle={{ fontWeight: 'bold', color: '#111827' }} />
                        <Bar dataKey="emails_sent" name="Emails Sent" fill="#9333ea" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 lg:p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Bounce Rate Trend</h2>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={emailTrends}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} domain={[0, 100]} width={30} />
                        <Tooltip contentStyle={{ backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #e5e7eb' }} labelStyle={{ fontWeight: 'bold', color: '#111827' }} />
                        <Line type="monotone" dataKey="bounce_rate" name="Bounce %" stroke="#f97316" strokeWidth={2} dot={{ fill: '#f97316', r: 4 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
